import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Loader2, CheckCircle2, AlertTriangle, Clock, Paperclip } from "lucide-react";
import { toast } from "sonner";

const STATUS_PILL = {
  sent: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  failed: { color: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  pending: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
};

export default function EmailLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [senderFilter, setSenderFilter] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (statusFilter) params.set("status", statusFilter);
      if (senderFilter) params.set("sender_email", senderFilter);
      const { data } = await api.get(`/email/logs?${params.toString()}`);
      setLogs(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, senderFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const f = q.trim().toLowerCase();
    if (!f) return logs;
    return logs.filter((l) =>
      [l.subject, l.user_name, l.sender_email, l.to?.join(" ")]
        .filter(Boolean)
        .some((x) => x.toLowerCase().includes(f))
    );
  }, [logs, q]);

  const senderList = useMemo(() => Array.from(new Set(logs.map((l) => l.sender_email))).filter(Boolean), [logs]);

  return (
    <div className="space-y-5">
      <div>
        <span className="label-overline">Communications</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Email Logs</h1>
        <p className="text-sm text-zinc-500 mt-2">Every email the HRMS sends is recorded here — sender, recipient, status, attachments.</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Search subject / recipient…" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" data-testid="email-logs-search" />
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36" data-testid="email-logs-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={senderFilter || "all"} onValueChange={(v) => setSenderFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Sender mailbox" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All senders</SelectItem>
            {senderList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-3 px-3">Status</th>
              <th className="py-3 px-3">From</th>
              <th className="py-3 px-3">To</th>
              <th className="py-3 px-3">Subject</th>
              <th className="py-3 px-3">Employee</th>
              <th className="py-3 px-3">Attached</th>
              <th className="py-3 px-3">When</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-10 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-zinc-400">No emails sent yet.</td></tr>
            ) : filtered.map((l) => {
              const pill = STATUS_PILL[l.status] || STATUS_PILL.pending;
              const Icon = pill.icon;
              return (
                <tr key={l.id} className="border-b border-zinc-100 last:border-0" data-testid={`email-log-${l.id}`}>
                  <td className="py-2.5 px-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${pill.color}`}>
                      <Icon className="w-3 h-3" /> {l.status.toUpperCase()}
                    </span>
                    {l.error && <div className="text-[10px] text-red-600 mt-1 max-w-[150px] truncate" title={l.error}>{l.error}</div>}
                  </td>
                  <td className="py-2.5 px-3 text-xs font-mono">{l.sender_email}</td>
                  <td className="py-2.5 px-3 text-xs">{(l.to || []).join(", ")}</td>
                  <td className="py-2.5 px-3 text-sm font-medium truncate max-w-[260px]">{l.subject}</td>
                  <td className="py-2.5 px-3 text-xs">{l.user_name || "—"}</td>
                  <td className="py-2.5 px-3 text-xs text-zinc-600">
                    {l.attachment_count > 0 ? (
                      <span className="inline-flex items-center gap-1" title={(l.attachment_names || []).join(", ")}>
                        <Paperclip className="w-3 h-3" /> {l.attachment_count}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="py-2.5 px-3 text-xs text-zinc-500">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
